import {
  BreifInfluencerV2StatusEnum,
  SORT_ORDER,
} from "@entities/brief/lib/constants";

import { AddCreator } from "@features/AddCreator";
import { BackednResponse } from "@shared/lib/types";

import { useGetBriefInfluencerPendingReviewFacets } from "@entities/brief/lib/hooks/useGetBriefInfluencerPendingReviewFacets";
import { useGetBriefInfluencerSearchFacets } from "@entities/brief/lib/hooks/useGetBriefInfluencerSearchFacets";
import { useGetBriefInfluencersV2 } from "@entities/brief/lib/hooks/useGetBriefInfluencersV2";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import BriefNotifications from "@features/BriefNotifications/BriefNotifications";
import { SocialMediaInfluencerComp } from "@features/socialMediaInfluencer/SocialMediaInfluencerComp";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import {
  BRAND_COST_STATUS,
  IBriefInfluencerV2,
  IGetBriefInfluencersV2Response,
  OFFER_STATUS,
} from "@shared/lib/types/briefInfluencerV2";
import type { InfiniteData } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer, Flex } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { getCampaignBriefStageCounts } from "./lib/campaignBriefFilterFacetCounts";
import {
  AgencyPendingReviewFlag,
  getAgencyPendingReviewChips,
  getAgencyPendingReviewSearchParams,
} from "./lib/campaignBriefPendingReview";
import {
  CampaignBriefStage,
  getCampaignBriefStageSearchParams,
} from "./lib/campaignBriefStage";
import {
  getInfluencerColumnStatusSearchParams,
  getShowDeclinedCreatorsSearchParams,
} from "./lib/influencerColumnStatusFilter";
import { useBriefInfluencerFilterStore } from "./store/useBriefInfluencerFilterStore";
import styles from "./ui/CampaignBrief.module.scss";
import {
  CampaignBriefFilters,
  type StatusFilter,
} from "./ui/CampaignBriefFilters";
import { CampaignBriefPendingReviewBar } from "./ui/CampaignBriefPendingReviewBar";
import { CampaignBriefTable } from "./ui/CampaignBriefTable/CampaignBriefTable";

export function CampaignBrief() {
  const { briefId } = useBriefStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState<StatusFilter>(
    CampaignBriefStage.Reviewing
  );

  const { searchFacets } = useGetBriefInfluencerSearchFacets(briefId || "");
  const { pendingReviewFacets } = useGetBriefInfluencerPendingReviewFacets({
    briefId: briefId || "",

    statuses:
      activeStatusFilter === CampaignBriefStage.Purchased
        ? [BreifInfluencerV2StatusEnum.Purchased]
        : [
            BreifInfluencerV2StatusEnum.Shortlisted,
            BreifInfluencerV2StatusEnum.Pricing,
          ],
    isShortListed:
      activeStatusFilter === CampaignBriefStage.Purchased
        ? undefined
        : activeStatusFilter === CampaignBriefStage.Shortlisted,
  });

  const stageCounts = getCampaignBriefStageCounts(searchFacets);
  const [activePendingReviewFlag, setActivePendingReviewFlag] =
    useState<AgencyPendingReviewFlag | null>(null);
  const [isAddCreatorModalOpen, setIsAddCreatorModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const { beFilters, resetFilters, saveFilters, setStatusFilter } =
    useBriefInfluencerFilterStore();
  const { userDetails } = useGetMe();
  const userId = userDetails?.userEntity?.id;

  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);

  const handleAddCreatorSuccess = () => {
    setIsAddCreatorModalOpen(false);
    setActiveStatusFilter(CampaignBriefStage.Reviewing);
    setActivePendingReviewFlag(null);
    resetFilters();
    setShouldScrollToTop(true);
  };

  const searchParams = useMemo(() => {
    const offerStatusFilter = [
      ...(beFilters.statusFilters?.isInfluencerCommercialPending
        ? [OFFER_STATUS["offer-yet-to-be-sent"], OFFER_STATUS["offer-sent"]]
        : []),
      ...(beFilters.statusFilters?.isInfluencerCommercialNegoRequested
        ? [OFFER_STATUS["negotiations-requested"]]
        : []),
      ...(beFilters.statusFilters?.isInfluencerCommercialReceived
        ? [OFFER_STATUS["offer-accepted"]]
        : []),

      ...(beFilters.statusFilters?.isInfluencerCommercialNegoReceived
        ? [OFFER_STATUS["negotiations-accepted"]]
        : []),
    ];

    const columnStatusFilters = beFilters.columnStatusFilters ?? [];
    const isColumnStatusFilterActive = columnStatusFilters.length > 0;
    const stageSearchParams =
      getCampaignBriefStageSearchParams(activeStatusFilter);
    const columnStatusParams = getInfluencerColumnStatusSearchParams(
      columnStatusFilters[0],
      activeStatusFilter
    );
    const showDeclinedCreatorsParams = !isColumnStatusFilterActive
      ? getShowDeclinedCreatorsSearchParams(
          beFilters.showDeclinedCreators,
          activeStatusFilter
        )
      : {};

    const isInfluencerCommercialFilterActive =
      beFilters.statusFilters?.isInfluencerCommercialPending ||
      beFilters.statusFilters?.isInfluencerCommercialNegoRequested ||
      beFilters.statusFilters?.isInfluencerCommercialReceived ||
      beFilters.statusFilters?.isInfluencerCommercialNegoReceived;

    const isBrandCommercialFilterActive =
      beFilters.statusFilters?.isPendingQuote ||
      beFilters.statusFilters?.isNegotiating ||
      beFilters.statusFilters?.isOfferReceived;

    const isCommercialFilterActive =
      isInfluencerCommercialFilterActive || isBrandCommercialFilterActive;

    const influencerCostDate =
      beFilters.influencerCostDate?.from || beFilters.influencerCostDate?.to
        ? beFilters.influencerCostDate
        : undefined;

    return {
      briefId,
      limit: 25,
      brandCostStatus: [
        ...(beFilters.statusFilters?.isOfferReceived
          ? [
              BRAND_COST_STATUS["First-Quote-Recieved"],
              BRAND_COST_STATUS["New-Quote-Recieved"],
            ]
          : []),
        ...(beFilters.statusFilters?.isNegotiating
          ? [BRAND_COST_STATUS["Negotiations-Requested"]]
          : []),
      ],
      ...(offerStatusFilter.length > 0 || influencerCostDate
        ? {
            influencerCost: {
              ...(offerStatusFilter.length > 0
                ? { offerStatus: offerStatusFilter }
                : {}),
              ...(influencerCostDate ? { date: influencerCostDate } : {}),
            },
          }
        : {}),
      ...(!isColumnStatusFilterActive
        ? {
            isInitialBrandCostPending: beFilters.statusFilters?.isPendingQuote
              ? true
              : undefined,
            isDeliverablesPending: beFilters.statusFilters
              ?.isPendingDeliverables
              ? true
              : undefined,
            isCreatorOnboardPending:
              beFilters.statusFilters?.isCreatorOnboardingPending || undefined,
          }
        : {}),
      deliverableV2Ids: [...(beFilters.delivrables || [])],
      isDeclined: beFilters.statusFilters?.isDeclined || undefined,
      dynamicFields: beFilters.dynamicFields || undefined,
      searchQuery: debouncedSearchQuery || undefined,
      sortByFollowers: beFilters.sortBy?.followers || undefined,
      sortByBrandCost: beFilters.sortBy?.brandCost || undefined,
      sortByDynamicFields: beFilters.sortBy?.sortByDynamicFields || undefined,
      sortByBriefFitEvalutionScore:
        beFilters.sortBy?.sortByBriefFitEvalutionScore || undefined,
      sortByCreatedAt: beFilters.sortByCreatedAt || undefined,
      sortByUpdatedAt: beFilters.sortByUpdatedAt || undefined,
      createdAt:
        beFilters.createdAt?.from || beFilters.createdAt?.to
          ? beFilters.createdAt
          : undefined,
      updatedAt:
        beFilters.updatedAt?.from || beFilters.updatedAt?.to
          ? beFilters.updatedAt
          : undefined,
      sortById:
        beFilters.sortBy?.followers ||
        beFilters.sortBy?.brandCost ||
        beFilters.sortByCreatedAt ||
        beFilters.sortByUpdatedAt
          ? undefined
          : SORT_ORDER.DESCEND,
      primaryContact: {
        ...(beFilters?.primaryContact || {}),
        type: beFilters?.primaryContact?.type ?? [],
      },
      assignedPricingPocV2:
        beFilters.assignedPricingPocV2 &&
        beFilters.assignedPricingPocV2.length > 0
          ? beFilters.assignedPricingPocV2
          : undefined,
      ...(isCommercialFilterActive
        ? { isCreatorDeclined: false, isBrandDeclined: false }
        : {}),
      ...stageSearchParams,
      ...columnStatusParams,
      ...showDeclinedCreatorsParams,
      ...getAgencyPendingReviewSearchParams(activePendingReviewFlag),
      socialMediaPlatforms: beFilters.socialMediaPlatforms || [],
    };
  }, [
    briefId,
    activeStatusFilter,
    activePendingReviewFlag,
    beFilters.statusFilters?.isOfferReceived,
    beFilters.statusFilters?.isNegotiating,
    beFilters.statusFilters?.isPendingQuote,
    beFilters.statusFilters?.isPendingDeliverables,
    beFilters.delivrables,
    beFilters.statusFilters?.isDeclined,
    beFilters.statusFilters?.isInfluencerCommercialPending,
    beFilters.statusFilters?.isInfluencerCommercialNegoRequested,
    beFilters.statusFilters?.isInfluencerCommercialReceived,
    beFilters.statusFilters?.isInfluencerCommercialNegoReceived,
    beFilters.dynamicFields,
    debouncedSearchQuery,
    beFilters.sortBy?.followers,
    beFilters.sortBy?.brandCost,
    beFilters.primaryContact,
    beFilters.assignedPricingPocV2,
    beFilters.statusFilters?.isCreatorOnboardingPending,
    beFilters.columnStatusFilters,
    beFilters.showDeclinedCreators,
    beFilters.sortBy?.sortByDynamicFields,
    beFilters.sortBy?.sortByBriefFitEvalutionScore,
    beFilters.sortByCreatedAt,
    beFilters.sortByUpdatedAt,
    beFilters.createdAt,
    beFilters.updatedAt,
    beFilters.influencerCostDate,
    beFilters.socialMediaPlatforms,
  ]);

  const {
    briefInfluencers: flatData,
    pagination,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    queryKey,
    refetch,
    isLoading,
  } = useGetBriefInfluencersV2(searchParams, Boolean(briefId));

  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [uncheckList, setUncheckList] = useState<string[]>([]);

  useEffect(() => {
    setSelectAll(false);
    setCheckedList([]);
    setUncheckList([]);
  }, [beFilters, activeStatusFilter]);

  const selectedRows = useMemo(() => {
    const rows = flatData || [];
    if (selectAll) {
      return rows.filter(
        (influencer) => !uncheckList.includes(influencer._id)
      );
    }
    return rows.filter((influencer) => checkedList.includes(influencer._id));
  }, [flatData, checkedList, selectAll, uncheckList]);

  const handleStatusFilterChange = useCallback(
    (status: StatusFilter) => {
      setActiveStatusFilter(status);
      setActivePendingReviewFlag(null);
      resetFilters();
      setSelectAll(false);
      setCheckedList([]);
      setUncheckList([]);
    },
    [resetFilters]
  );

  useEffect(() => {
    if (!activePendingReviewFlag) return;
    const chips = getAgencyPendingReviewChips(pendingReviewFacets);
    if (!chips.some((chip) => chip.id === activePendingReviewFlag)) {
      setActivePendingReviewFlag(null);
    }
  }, [activePendingReviewFlag, pendingReviewFacets]);

  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const updateBriefInfluencer = (id: string, data: IBriefInfluencerV2) => {
    queryClient.setQueryData<
      InfiniteData<BackednResponse<IGetBriefInfluencersV2Response>>
    >(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            data:
              page.data?.data?.map((inf) =>
                inf._id === id ? { ...data } : inf
              ) ?? [],
          },
        })),
      };
    });
  };

  const onAddCreator = () => {
    setIsAddCreatorModalOpen(true);
  };

  const updateBriefInfluencers = (
    data: { id: string; briefInfluencer: IBriefInfluencerV2 }[]
  ) => {
    queryClient.setQueryData<
      InfiniteData<BackednResponse<IGetBriefInfluencersV2Response>>
    >(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) => ({
          ...page,
          data: {
            ...page.data,
            data:
              page.data?.data?.map((inf) => {
                const update = data.find((d) => d.id === inf._id);
                return update ? { ...inf, ...update.briefInfluencer } : inf;
              }) ?? [],
          },
        })),
      };
    });
  };

  const [selectedInfluencer, setSelectedInfluencer] = useState<{
    visible: boolean;
    props?: {
      username?: string;
      platform?: string;
      influencerId?: string;

      toOpenInfluencer?: boolean;
      creatorInfo: {
        name?: string;
        image?: string;
        username?: string;
      };
    };
  }>({ visible: false, props: undefined });

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <CampaignBriefTable
          setSelectedInfluencer={setSelectedInfluencer}
          isInAll={false}
          updateBriefInfluencer={updateBriefInfluencer}
          params={searchParams}
          title={
            <Flex vertical>
              <div className={styles.controlsBar}>
                <CampaignBriefFilters
                  reviewingCount={stageCounts[CampaignBriefStage.Reviewing]}
                  shortlistedCount={stageCounts[CampaignBriefStage.Shortlisted]}
                  purchasedCount={stageCounts[CampaignBriefStage.Purchased]}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  activeStatusFilter={activeStatusFilter}
                  onStatusFilterChange={handleStatusFilterChange}
                  searchParams={searchParams}
                  selectedRows={selectedRows}
                  onAddCreator={onAddCreator}
                />
              </div>
              <div className={styles.pendingReviewWrap}>
                <CampaignBriefPendingReviewBar
                  facets={pendingReviewFacets}
                  activeFlag={activePendingReviewFlag}
                  filterParams={searchParams}
                  onFlagChange={(flag) => {
                    setActivePendingReviewFlag(flag);
                    setSelectAll(false);
                    setCheckedList([]);
                    setUncheckList([]);
                    setShouldScrollToTop(true);
                  }}
                />
              </div>
              {pagination ? (
                <Flex
                  justify="space-between"
                  align="center"
                  style={{
                    padding: "0.75rem 1.5rem",
                    borderTop: "1px solid #e3e8ee",
                  }}
                >
                  <span
                    className={styles.tableTitle}
                  >{`${pagination.totalCount} results found`}</span>
                </Flex>
              ) : null}
            </Flex>
          }
          briefInfluencers={flatData || []}
          refetch={refetch}
          pagination={pagination}
          isLoadingFirstPage={isLoading}
          isLoadingMore={isFetchingNextPage}
          onLoadMore={handleLoadMore}
          selectAll={selectAll}
          checkedList={checkedList}
          uncheckList={uncheckList}
          setSelectAll={setSelectAll}
          setCheckedList={setCheckedList}
          setUncheckList={setUncheckList}
          activeStatusFilter={activeStatusFilter}
          onStatusFilterChange={handleStatusFilterChange}
          onAddCreator={onAddCreator}
          updateBriefInfluencers={updateBriefInfluencers}
          debouncedSearchQuery={debouncedSearchQuery}
          shouldScrollToTop={shouldScrollToTop}
          onScrollToTopDone={() => setShouldScrollToTop(false)}
          searchFacets={searchFacets}
        />
      </div>
      <AddCreator
        isOpen={isAddCreatorModalOpen}
        onClose={() => setIsAddCreatorModalOpen(false)}
        onSuccess={handleAddCreatorSuccess}
      />
      <Drawer
        title="Influencer Details"
        open={selectedInfluencer.visible}
        width={1300}
        footer={null}
        destroyOnClose
        onClose={() =>
          setSelectedInfluencer({ visible: false, props: undefined })
        }
      >
        {selectedInfluencer.props?.creatorInfo && (
          <SocialMediaInfluencerComp
            creatorInfo={selectedInfluencer.props?.creatorInfo}
            socialMediaInfluencerReq={
              selectedInfluencer.props?.influencerId
                ? {
                    toOpenInfluencer:
                      selectedInfluencer.props?.toOpenInfluencer,
                    influencerId: selectedInfluencer.props?.influencerId || "",
                  }
                : {
                    username: {
                      platform: selectedInfluencer.props?.platform || "",
                      socialMediaUsername:
                        selectedInfluencer.props?.username || "",
                    },
                    toOpenInfluencer:
                      selectedInfluencer.props?.toOpenInfluencer,
                  }
            }
          />
        )}
      </Drawer>
      <BriefNotifications
        briefId={briefId || ""}
        selectedUserId={userId || ""}
        type="brief"
      />
    </div>
  );
}
